<script>
/**
 * 3D 运行大屏：BasicLayout 里的菜单页（也是默认主页），读数区铺满内容格。
 *
 * 数据只走四个已有接口，不新增后端：
 *   1) /five/month/share/data  按日的检测人数/应检人数 → 环形柱状与历史天的选日明细（这张日报表当天没行，今日不从这里取）
 *   2) /share/permission/list  在册司机与账号状态 → 车队状态面板
 *   3) /user/test/data/list    两路都显式传司机名单 user_id 数组（与分母同一批人）：一路按最近几天 time 区间取最新若干条 → 实时检测流 + 中央芯片的气体吸附强度；
 *                              另一路按当日 time 区间翻页取回 → 用它的 totalCount 与当日行算今日人次/人数/超标
 *   4) /user/attendance/stat/load 今日考勤明细（uid 数组 + date）→ 已打卡/未打卡人数
 * 实时流与今日统计都是“一次按名单收口的查询”，不再逐司机并发（返回行里没有 user_id，根本攒不出“上一轮活跃司机”名单，
 * 那样固定查 roster 前几个会把其余几百个司机的新记录永久排除在外）。
 */
import moment from 'moment'
import { loadKeiyoFiveMonthShareData, loadPermissionList, loadTestDataList, loadUserAttendanceRecord } from '@/api/data'
import DashboardScene from './dashboardScene'

const DAY_WINDOW = 30 // 环形柱状的天数，和场景里的 MAX_BARS 对齐
const STREAM_PAGE_SIZE = 24 // 实时流单次取回条数（服务端按 create_time desc，第一页就是最新的）
const STREAM_SHOW = 14 // 流里最终显示几项
const STREAM_WINDOW_DAYS = 7 // 回看 7 天：周末没人吹气时流不至于整块空掉
const REFRESH_INTERVAL = 60000 // 大屏是常驻页面，60s 一轮足够
const TODAY_STAT_LIMIT = 500 // 当日记录单页取回上限
const TODAY_STAT_MAX_PAGE = 4 // 最多翻 4 页（2000 行）；人次走 totalCount 不受此限，去重类数字按已取回这批算

// 与检测记录页同一套判定口径（原始值阈值，展示值 ×5）
const RAW_WARN = 0.03
const RAW_PASS = 0.015

export default {
  name: 'Dashboard3d',
  data () {
    return {
      loading: true,
      webglFail: false,
      series: [],
      events: [],
      selectedDay: null,
      drivers: 0,
      activeAccounts: 0,
      kpi: {
        coverage: 0,
        // 今日已检人数（不是人次）：顶栏四个数与左侧今日未检必须同一口径，否则“已检 + 未检 ≠ 在册人数”会读成错数
        tested: 0,
        limited: 0,
        // 今日已打卡人数（考勤只看当日）
        attendance: 0
      },
      // 今日实时统计 { count 人次, users 人数, limited 超标人数 }；日报表当天没有行，靠它补
      todayStat: null,
      // 今日已检人数（去重）：今日未检与完成率都用人数口径，不能拿人次去减启用账号数
      todayUsers: 0,
      // 数字滚动用的显示值，和真实值分开，避免每帧改数据触发整页 diff
      shown: {
        coverage: 0,
        tested: 0,
        limited: 0,
        attendance: 0
      },
      refreshing: false,
      rangeText: ''
    }
  },
  computed: {
    // 今日未检 = 启用账号数 - 今日已检人数（都是人数口径，且分子就是按这份名单查出来的）；
    // 归零只是防护，不让补算或翻页取回更多记录时跑出负数
    idleToday () {
      if (this.coverage >= 100) return 0
      return Math.max(this.activeAccounts - this.todayUsers, 0)
    },
    // 今日未打卡：和今日未检同一口径，两个数一起看就能区分"人没来"还是"来了没吹气"
    idleAttendance () {
      return Math.max(this.activeAccounts - this.kpi.attendance, 0)
    },
    // 出勤率跟今日检测完成率同一分母（启用账号），两个百分比直接可比
    attendanceRate () {
      return this.activeAccounts ? Math.round(this.kpi.attendance * 100 / this.activeAccounts) : 0
    },
    coverage () {
      return this.kpi.coverage
    }
  },
  created () {
    // viewer 刻意不进 data：three 的场景图有成百上千个对象，
    // 交给 Vue 做响应式会把首屏卡在 observer 上
    this.viewer = null
    this.timer = null
    this.tweenRaf = null
    // 在册司机的归人索引（只给今日统计用，不必响应式）与待查名单
    this.rosterAccounts = {}
    this.rosterNames = {}
    this.candidateIds = []
  },
  mounted () {
    // 只在这一页挂上 body 标记，让内容区换成纵向 flex（全局样式块），读数区才能把剩余高度吃满
    document.body.classList.add('db3d-page')
    this.initViewer()
    this.loadData()
    this.timer = setInterval(() => {
      if (!document.hidden) this.loadData()
    }, REFRESH_INTERVAL)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    document.body.classList.remove('db3d-page')
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    if (this.tweenRaf) {
      cancelAnimationFrame(this.tweenRaf)
      this.tweenRaf = null
    }
    window.removeEventListener('resize', this.handleResize)
    if (this.viewer) {
      this.viewer.dispose()
      this.viewer = null
    }
  },
  methods: {
    initViewer () {
      try {
        this.viewer = new DashboardScene(this.$refs.stage, {
          onSelectDay: (index) => this.pickDay(index)
        })
      } catch (e) {
        // WebGL 上下文创建失败（无 GPU 驱动 / 浏览器禁用）时页面仍要能读出数
        console.log('[dashboard3d] scene init failed', String(e && e.message))
        this.webglFail = true
        this.viewer = null
      }
    },
    async loadData () {
      // 今日统计要把司机名单当查询条件，所以必须等 roster 落地；日报序列与它无关，并行拉
      await Promise.all([this.loadSeries(), this.loadRoster()])
      await this.loadTodayStat()
      // 今日的数不在日报表里，roster 与实时结果都到位后才能补进序列和顶栏
      this.applyTodayStat()
      // 考勤要拿账号名单当入参，所以排在 roster 之后
      await this.loadAttendanceToday()
      this.tweenKpi(['tested', 'coverage', 'limited', 'attendance'])
      await this.loadEvents()
      this.loading = false
    },
    async loadSeries () {
      let map = {}
      try {
        const resp = await loadKeiyoFiveMonthShareData({ month: moment().format('YYYY-MM') })
        map = (resp && resp.data) || {}
      } catch (e) {
        console.log('[dashboard3d] share data load failed', String(e && e.message))
        return
      }
      const series = []
      for (let i = DAY_WINDOW - 1; i >= 0; i--) {
        const day = moment().subtract(i, 'days')
        const key = day.format('YYYY-MM-DD')
        const info = map[key] || {}
        series.push({
          date: key,
          label: day.format('MM-DD'),
          testUNum: Number(info.test_u_num || 0),
          totalUNum: Number(info.total_u_num || 0),
          limitedUser: Number(info.limited_user || 0),
          percent: Number(info.percent || 0)
        })
      }
      this.series = series
      if (this.viewer) this.viewer.setSeries(series)
      // 默认跟着今天走，用户点过别的日子之后刷新不抢回选择
      if (!this.selectedDay) this.pickDay(series.length - 1)
      this.rangeText = `${series[0].label} ~ ${series[series.length - 1].label}`
      // 面板上的统计项全部只看今日，30 天的数据只用来喂环形柱；
      // 今日那一格日报表里必然是空的，不在这里取，交给 applyTodayStat 用实时结果补
    },
    async loadTodayStat () {
      // 日报表（keiyo_share_daily_data）是定时任务在凌晨 00:0x 写“昨天”的行（h === 0 && date = lastDayStr），
      // 所以当天全天都没有今日行，三个今日数字只能实时算。
      // 必须显式传名单 user_id：不传时服务端只按“当前登录者自己绑定的司机”收口，而组织账号的名单（分母）
      // 是含下级组织的全员，admin_tag=1 登录更是完全不收口 —— 分子分母不同源，完成率会顶过 100%、今日未检归 0
      const ids = this.candidateIds || []
      if (ids.length === 0) {
        this.todayStat = null
        return
      }
      const day = moment().format('YYYY-MM-DD')
      try {
        const rows = []
        let total = 0
        for (let page = 1; page <= TODAY_STAT_MAX_PAGE; page++) {
          const resp = await loadTestDataList({
            pageNo: page,
            pageSize: TODAY_STAT_LIMIT,
            user_id: ids,
            time: [`${day} 00:00:00`, `${day} 23:59:59`]
          })
          const result = (resp && resp.result) || {}
          const part = result.data || []
          total = Number(result.totalCount || 0)
          for (const row of part) rows.push(row)
          // 已经拿全（或当日确实没这么多）就停，别让大屏每轮都去抓空页
          if (rows.length >= total || part.length < TODAY_STAT_LIMIT) break
        }
        const users = {}
        const over = {}
        for (const row of rows) {
          // 接口返回行里没有 user_id，只能靠 account 往名单上归（日报的服务端口径是按 user_id 算的）；
          // 归不到人的行（account 空、或不是这批在册司机）不计入人数，否则“一条记录一个人”会把人数算成人次
          const key = this.driverKey(row)
          if (!key) continue
          users[key] = true
          // 超标口径与日报 xlsx 的「基準値オーバー」一致：原始值 >= 0.03
          if (Number(row.measure_value || 0) >= RAW_WARN) over[key] = true
        }
        this.todayStat = {
          // 人次用服务端的 totalCount，即使当日记录超过翻页上限也精确；
          // 两个去重类数字只看已取回这批，当日条数超过 TODAY_STAT_LIMIT * TODAY_STAT_MAX_PAGE 时它们会是下限
          count: total || rows.length,
          users: Object.keys(users).length,
          limited: Object.keys(over).length
        }
      } catch (e) {
        console.log('[dashboard3d] today stat load failed', String(e && e.message))
      }
    },
    // 把一条检测记录归到在册司机头上：优先 account，退到能唯一命中的姓名，都对不上就当无法归人
    driverKey (row) {
      const account = String(row.account || '').trim().toLowerCase()
      if (account && this.rosterAccounts[account]) return 'a' + account
      const name = String(row.name || '').trim()
      if (name && this.rosterNames[name] === 1) return 'n' + name
      return ''
    },
    applyTodayStat () {
      const idx = this.series.length - 1
      if (idx < 0) return
      const today = this.series[idx]
      const stat = this.todayStat
      if (stat) {
        // 应检分母跟「今日出勤/今日未检」同一个（启用账号），同屏两个百分比才可直接对比
        const total = this.activeAccounts
        today.testUNum = stat.users
        today.totalUNum = total
        today.limitedUser = stat.limited
        today.percent = total ? Math.round(stat.users * 100 / total) : 0
        if (this.viewer) this.viewer.setSeries(this.series)
        // 顶栏走人数口径（人次不丢掉，降到选中日期卡同一行的轻字后缀）
        this.kpi.tested = stat.users
      } else {
        this.kpi.tested = today.testUNum
      }
      this.todayUsers = today.testUNum
      this.kpi.coverage = today.percent
      this.kpi.limited = today.limitedUser
      // selectedDay 指的就是 series 里这个对象，直接就地改属性就能让选日卡跟着刷新
    },
    async loadAttendanceToday () {
      // 考勤接口没有按司机名单聚合的 count，用"当日明细 + 前端按 uid 去重"：一天一个请求，
      // 服务端单次上限 1000 条（约 150 人 × 6 次打刻），再大的车队需要后端补一个聚合接口
      if (!this.candidateIds || this.candidateIds.length === 0) {
        this.kpi.attendance = 0
        return
      }
      try {
        const resp = await loadUserAttendanceRecord({
          uid: this.candidateIds,
          date: moment().format('YYYY-MM-DD')
        })
        const records = (resp && resp.data) || []
        const ids = {}
        for (const item of records) {
          ids[item.uid] = true
        }
        this.kpi.attendance = Object.keys(ids).length
      } catch (e) {
        console.log('[dashboard3d] attendance load failed', String(e && e.message))
      }
    },
    async loadRoster () {
      try {
        const resp = await loadPermissionList({ type: 'all', pageNo: 1, pageSize: 1000 })
        const list = (resp && resp.result && resp.result.data) || []
        let active = 0
        const accounts = {}
        const names = {}
        for (const item of list) {
          if (item.delete_tag !== 1 && item.status !== 0) {
            active++
            // 当日的记录行没有 user_id，只能拿这份名单把记录归到人；
            // 姓名只用来兜 account 缺失的记录，重名就当无法归人，不能猜
            const account = String(item.account || '').trim().toLowerCase()
            if (account) accounts[account] = true
            const name = String(item.name || '').trim()
            names[name] = (names[name] || 0) + 1
          }
        }
        this.rosterAccounts = accounts
        this.rosterNames = names
        this.drivers = list.length
        this.activeAccounts = active
        // 近端读数的候选池：只拿启用的账号，刷新时再按上一轮的活跃度排序
        this.candidateIds = list
          .filter(item => item.delete_tag !== 1 && item.status !== 0)
          .map(item => item.user_id)
          .filter(id => typeof id === 'number')
      } catch (e) {
        console.log('[dashboard3d] roster load failed', String(e && e.message))
      }
    },
    async loadEvents () {
      // 一次查询拿“名单内最近 STREAM_WINDOW_DAYS 天的最新若干条”：不传 time 的话服务端会按会员等级
      // 把部分司机限到只看近 2 天（admin.js loadUserTestDataList 的 else 分支），那样刚做的检测也可能读不到，
      // 所以这里自己给一个确定区间；user_id 同样显式传名单，与今日统计同一批人
      const ids = this.candidateIds || []
      if (ids.length === 0) return
      try {
        const resp = await loadTestDataList({
          pageNo: 1,
          pageSize: STREAM_PAGE_SIZE,
          user_id: ids,
          time: [
            moment().subtract(STREAM_WINDOW_DAYS, 'days').format('YYYY-MM-DD HH:mm:ss'),
            moment().format('YYYY-MM-DD HH:mm:ss')
          ]
        })
        const rows = ((resp && resp.result && resp.result.data) || []).slice()
        // 不只依赖服务端的排序，前端再排一次，保证排在前面的就是时间新的
        rows.sort((a, b) => Number(b.create_time_stamp) - Number(a.create_time_stamp))
        this.events = rows.slice(0, STREAM_SHOW).map(record => this.toEvent(record))
        this.applyGas(this.events[0])
      } catch (e) {
        console.log('[dashboard3d] stream load failed', String(e && e.message))
      }
    },
    toEvent (record) {
      const raw = Number(record.measure_value || 0)
      return {
        // 行里没有 user_id（只有 record_id），所以姓名只能从 name / account 里取
        name: record.name || record.account || '-',
        time: moment(record.create_time_stamp).format('MM-DD HH:mm'),
        // value 只能是纯数字串：applyGas 直接拿 Number(event.value) 做归一，
        // 单位是模板里另起一个 span 拼上去的，写进这里会算出 NaN，把气体粒子的吸附动画打停
        value: this.formatValue(raw),
        tone: this.toneOf(raw),
        city: record.location && record.location.city_name ? record.location.city_name : ''
      }
    },
    applyGas (event) {
      if (!this.viewer) return
      if (!event) {
        this.viewer.setGas(0.12, 'pass')
        return
      }
      // 读数越大吸附越急：以醉驾线 0.15 的一半为满强度做归一
      const intensity = Math.max(0.12, Math.min(Number(event.value) / 0.08, 1))
      this.viewer.setGas(intensity, event.tone)
    },
    toneOf (raw) {
      if (raw < RAW_PASS) return 'pass'
      if (raw < RAW_WARN) return 'warn'
      return 'fail'
    },
    meterWidth (day) {
      // 完成率条封顶 100%，服务端偶尔会把补算的日子算超
      return `${Math.min(Number(day.percent) || 0, 100)}%`
    },
    formatValue: (value) => {
      if (value < 0.015) return '0.00'
      return (value * 5).toFixed(2)
    },
    pickDay (index) {
      const day = this.series[index]
      if (!day) return
      this.selectedDay = day
    },
    isToday (day) {
      return !!day && day.date === moment().format('YYYY-MM-DD')
    },
    handleResize () {
      if (this.viewer) this.viewer.resize()
    },
    tweenKpi (keys) {
      // 大屏的数字不该是"跳"出来的：一次 700ms 的补间，rAF 驱动，页面销毁时收掉
      const from = {}
      const to = {}
      for (const key of keys) {
        from[key] = this.shown[key]
        to[key] = this.kpi[key]
      }
      const start = performance.now()
      const step = (now) => {
        const t = Math.min((now - start) / 700, 1)
        const ease = 1 - Math.pow(1 - t, 3)
        for (const key of keys) {
          this.shown[key] = Math.round(from[key] + (to[key] - from[key]) * ease)
        }
        if (t < 1) {
          this.tweenRaf = requestAnimationFrame(step)
        } else {
          this.tweenRaf = null
        }
      }
      if (this.tweenRaf) cancelAnimationFrame(this.tweenRaf)
      this.tweenRaf = requestAnimationFrame(step)
    },
    tilt (event, side) {
      // 面板随鼠标做 ±2.4° 的透视偏转，是"3D"里最便宜的一层
      const now = performance.now()
      if (now - (this._tiltAt || 0) < 40) return
      this._tiltAt = now
      const root = this.$refs.shell
      if (!root) return
      // 以读数区自己的矩形算，不拿窗口尺寸：进了布局后这一块只占视口一部分
      const rect = root.getBoundingClientRect()
      const ratio = (event.clientX - rect.left) / (rect.width || 1) - 0.5
      const pitch = (event.clientY - rect.top) / (rect.height || 1) - 0.5
      root.style.setProperty('--tilt-y', `${(side === 'left' ? -1 : 1) * ratio * 2.4}deg`)
      root.style.setProperty('--tilt-x', `${-pitch * 1.6}deg`)
    },
    async refresh () {
      // 手动刷新走和 60s 轮询同一条路；不抹 loading 遮罩，只是把数据重拉一遍
      if (this.refreshing) return
      this.refreshing = true
      try {
        await this.loadData()
      } finally {
        this.refreshing = false
      }
    }
  }
}
</script>

<template>
  <div class="db3d" ref="shell" @mousemove="tilt($event, 'right')">
    <!-- WebGL 画布：OrbitControls 只吃这块的指针事件，面板不会被它拦 -->
    <div class="db3d__stage" ref="stage"></div>
    <div class="db3d__vignette"></div>

    <header class="db3d__top">
      <div class="db3d__brand">
        <div class="db3d__title">{{ $t('db3d.title') }}</div>
        <div class="db3d__subtitle">{{ $t('db3d.subtitle') }}</div>
      </div>
      <div class="db3d__kpis">
        <div class="db-kpi">
          <div class="db-kpi__label">{{ $t('db3d.kpi.coverage') }}</div>
          <div class="db-kpi__value">{{ shown.coverage }}<span class="db-kpi__unit">%</span></div>
        </div>
        <div class="db-kpi">
          <div class="db-kpi__label">{{ $t('db3d.kpi.tested') }}</div>
          <div class="db-kpi__value">{{ shown.tested }}</div>
        </div>
        <div class="db-kpi">
          <div class="db-kpi__label">{{ $t('db3d.kpi.limited') }}</div>
          <div class="db-kpi__value db-kpi__value--fail">{{ shown.limited }}</div>
        </div>
        <div class="db-kpi">
          <div class="db-kpi__label">{{ $t('db3d.kpi.attendance') }}</div>
          <div class="db-kpi__value">{{ shown.attendance }}</div>
        </div>
      </div>
      <a class="db3d__refresh" :title="$t('db3d.refresh')" @click="refresh">
        <a-icon type="reload" :spin="refreshing" /><span>{{ $t('db3d.refresh') }}</span>
      </a>
    </header>

    <aside class="db3d__panel db3d__panel--left" @mousemove="tilt($event, 'left')">
      <section class="db-card">
        <div class="db-card__head">{{ $t('db3d.panel.fleet') }}</div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.fleet.drivers') }}</span>
          <span class="db-row__v">{{ drivers }}</span>
        </div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.fleet.active') }}</span>
          <span class="db-row__v">{{ activeAccounts }}</span>
        </div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.fleet.idle') }}</span>
          <span class="db-row__v db-row__v--warn">{{ idleToday }}</span>
        </div>
      </section>

      <section class="db-card">
        <div class="db-card__head">{{ $t('db3d.panel.attendance') }}</div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.attendance.checkin') }}</span>
          <span class="db-row__v" :class="{ 'db-row__v--mute': !kpi.attendance }">{{ kpi.attendance }}</span>
        </div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.attendance.checkinIdle') }}</span>
          <span class="db-row__v" :class="idleAttendance ? 'db-row__v--warn' : 'db-row__v--mute'">{{ idleAttendance }}</span>
        </div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.attendance.rate') }}</span>
          <span class="db-row__v">{{ attendanceRate }}<i class="db-row__u">%</i></span>
        </div>
      </section>

      <section class="db-card" v-if="selectedDay">
        <div class="db-card__head">
          {{ $t('db3d.panel.day') }}
          <span class="db-card__aside">{{ selectedDay.date }}</span>
        </div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.day.tested') }}</span>
          <span class="db-row__v">{{ selectedDay.testUNum }}<i
            v-if="isToday(selectedDay) && todayStat"
            class="db-row__u">· {{ todayStat.count }} {{ $t('db3d.unit.testNum') }}</i></span>
        </div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.day.total') }}</span>
          <span class="db-row__v">{{ selectedDay.totalUNum }}</span>
        </div>
        <div class="db-row">
          <span class="db-row__k">{{ $t('db3d.day.limited') }}</span>
          <!-- 只有今日这一格是新算出来的真超标；历史天日报表里的 limited_user 存的是会员失效人数，标错了不能再当真值用 -->
          <span
            class="db-row__v"
            :class="isToday(selectedDay) ? (selectedDay.limitedUser ? 'db-row__v--fail' : '') : 'db-row__v--mute'">{{ isToday(selectedDay) ? selectedDay.limitedUser : '--' }}</span>
        </div>
        <div class="db-meter">
          <div class="db-meter__bar">
            <div class="db-meter__fill" :style="{ width: meterWidth(selectedDay) }"></div>
          </div>
          <span class="db-meter__text">{{ selectedDay.percent }}%</span>
        </div>
      </section>
    </aside>

    <aside class="db3d__panel db3d__panel--right">
      <section class="db-card db-card--stream">
        <div class="db-card__head">{{ $t('db3d.panel.stream') }}</div>
        <transition-group name="db-stream" tag="div" class="db-stream">
          <div
            class="db-stream__item"
            :class="'db-stream__item--' + item.tone"
            v-for="(item, index) in events"
            :key="item.name + item.time + index">
            <span class="db-stream__dot"></span>
            <div class="db-stream__main">
              <div class="db-stream__name">{{ item.name }}</div>
              <div class="db-stream__meta">{{ item.time }}<template v-if="item.city"> · {{ item.city }}</template></div>
            </div>
            <div class="db-stream__value">{{ item.value }}<span class="db-stream__unit">mg/L</span></div>
          </div>
        </transition-group>
        <div class="db-empty" v-if="!loading && events.length === 0">{{ $t('db3d.empty.stream') }}</div>
      </section>
    </aside>

    <footer class="db3d__bottom">
      <div class="db-legend">
        <span class="db-legend__title">{{ $t('db3d.legend.title') }}</span>
        <span class="db-legend__item"><i class="db-legend__dot db-legend__dot--normal"></i>{{ $t('db3d.legend.normal') }}</span>
        <span class="db-legend__item"><i class="db-legend__dot db-legend__dot--warn"></i>{{ $t('user.test.appraise.warn') }}</span>
        <span class="db-legend__item"><i class="db-legend__dot db-legend__dot--fail"></i>{{ $t('user.test.appraise.no') }}</span>
      </div>
      <div class="db3d__hint">
        <span>{{ $t('db3d.range') }}: {{ rangeText }}</span>
        <span class="db3d__hint-sep"></span>
        <span>{{ $t('db3d.hint') }}</span>
      </div>
    </footer>

    <transition name="db-fade">
      <div class="db3d__overlay" v-if="loading || webglFail">
        <div v-if="webglFail" class="db3d__overlay-fail">{{ $t('db3d.webgl.fail') }}</div>
        <template v-else>
          <a-spin size="large" />
          <div class="db3d__overlay-text">{{ $t('db3d.loading') }}</div>
        </template>
      </div>
    </transition>
  </div>
</template>

<style lang="less">
// 内容列（.ant-layout.sidemenu）本身就是 min-height:100vh 的 flex column，
// .ant-layout-content 靠 flex:auto 已经拿到了剩余高度，但它的 height 是百分比（对父级解析不出定值），
// 所以子页写 100% 会塌。这里把内容区显式成纵向 flex，子页用 flex:1 分高度，
// 顶栏、内边距、页脚都由布局自己算，页面不再写 100vh 魔法数字。挂在 body 上只影响这一页
body.db3d-page .ant-layout-content {
  display: flex;
  flex-direction: column;
}
</style>

<style lang="less" scoped>
// 读数区铺满内容区：高度跟着 flex 走，只给一个下限，窗口太矮时让整页自然滚动
.db3d {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-height: 540px;
  overflow: hidden;
  border-radius: 6px;
  background: #0a131c;
  color: #c8d6e2;
  font-family: var(--font-ui);
  // 面板透视偏转的载体，由 JS 写 --tilt-x/--tilt-y
  --tilt-x: 0deg;
  --tilt-y: 0deg;
}

.db3d__stage {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  // canvas 是 three 运行时插进来的，拿不到 scoped 属性，必须穿透；
  // 宽高由 renderer.setSize 写成行内 style，这里只管显示方式
  ::v-deep canvas {
    display: block;
    outline: none;
  }
}

// 四角压暗 + 上下留白，保证压在场景上的文字读得清
.db3d__vignette {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  background:
    radial-gradient(120% 90% at 50% 46%, rgba(10, 19, 28, 0) 42%, rgba(6, 12, 18, 0.72) 100%),
    linear-gradient(180deg, rgba(6, 12, 18, 0.78) 0%, rgba(6, 12, 18, 0) 22%, rgba(6, 12, 18, 0) 74%, rgba(6, 12, 18, 0.7) 100%);
}

/* ── 顶栏 ── */
.db3d__top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  // 读数区高度有限，顶栏带只要包住 KPI 卡片（14 + ~70）就行，
  // 余下的高度不如让给两侧面板，免得卡片列被截
  height: 96px;
  padding: 14px 26px;
  display: flex;
  align-items: flex-start;
  gap: 24px;
  pointer-events: none;
}

.db3d__title {
  font-size: 21px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #f2f8fc;
}

.db3d__subtitle {
  margin-top: 5px;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: #8fa5b8;
}

.db3d__kpis {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.db-kpi {
  min-width: 150px;
  padding: 10px 16px 12px;
  border-radius: 4px;
  background: rgba(14, 26, 37, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(10px) saturate(120%);
  pointer-events: auto;
  // 顶栏卡片随整体透视，和两侧面板一副骨架
  transform: perspective(1200px) translateZ(0) rotateX(var(--tilt-x));
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    background: rgba(18, 32, 46, 0.62);
    border-color: rgba(56, 182, 240, 0.28);
  }

  &__label {
    font-size: 11px;
    letter-spacing: 0.05em;
    color: #8fa5b8;
  }

  &__value {
    margin-top: 2px;
    font-family: var(--font-data);
    font-size: 27px;
    font-weight: 600;
    line-height: 1.15;
    color: #f2f8fc;

    &--pass { color: #4eae7f; }
    &--warn { color: #d9a052; }
    &--fail { color: #e05c52; }
  }

  &__unit {
    margin-left: 3px;
    font-size: 12px;
    font-weight: 400;
    color: #6e8296;
  }
}

.db3d__refresh {
  pointer-events: auto;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  height: 32px;
  padding: 0 12px;
  white-space: nowrap;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(14, 26, 37, 0.55);
  color: #c8d6e2;
  font-size: 12px;
  backdrop-filter: blur(10px);
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: #38b6f0;
    border-color: rgba(56, 182, 240, 0.4);
  }
}

/* ── 两侧玻璃面板 ── */
.db3d__panel {
  position: absolute;
  // 卡片的底边跟顶栏 / 图例两条带子贴齐：这两个值是跟 .db3d__top 的 height 和
  // .db3d__bottom 的 min-height 对上的，改那边要同步改这里
  top: 96px;
  bottom: 56px;
  width: 292px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
  transform-style: preserve-3d;
  // 卡片的高度是靠内容顶起来的（flex 子项默认 min-height: auto，缩不下去），
  // 读数区一矮（笔记本视口被顶栏和页脚吃掉一截）最后一张卡就会被 .db3d 的 overflow 切到屏幕外。
  // 这里让面板自己成为滚动区：滚轮压在卡片上就能把剩下的读数带出来
  //（卡片是 pointer-events: auto，滚动手势顺着祖先链落到面板）
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.14);
    border-radius: 2px;
  }
}

.db3d__panel--left {
  left: 26px;
  // 左侧面板朝里侧倾，和右侧形成"舱内仪表台"的夹角
  transform: perspective(1400px) rotateY(7deg) rotateX(var(--tilt-x)) rotateZ(var(--tilt-y));
}

.db3d__panel--right {
  right: 26px;
  width: 316px;
  transform: perspective(1400px) rotateY(-7deg) rotateX(var(--tilt-x)) rotateZ(calc(var(--tilt-y) * -1));
}

.db-card {
  pointer-events: auto;
  padding: 14px 16px 15px;
  border-radius: 5px;
  background: rgba(12, 22, 32, 0.56);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 30px rgba(4, 10, 16, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(14px) saturate(125%);

  &--stream {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 12px;
    letter-spacing: 0.08em;
    color: #a9b8c6;
    padding-bottom: 10px;
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  &__aside {
    font-family: var(--font-data);
    font-size: 11px;
    color: #6e8296;
  }
}

.db-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;

  &__k {
    font-size: 12px;
    color: #8fa5b8;
  }

  &__v {
    font-family: var(--font-data);
    font-size: 15px;
    font-weight: 600;
    color: #f2f8fc;

    &--warn { color: #d9a052; }
    &--fail { color: #e05c52; }
    &--mute { color: rgba(143, 165, 184, 0.45); }
  }

  // 行尾单位，比数值小一号且不参与字重，不让 % 抢读数
  &__u {
    font-style: normal;
    font-size: 11px;
    font-weight: 400;
    margin-left: 2px;
    color: #6e8296;
  }
}

.db-meter {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;

  &__bar {
    flex: 1;
    height: 3px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.07);
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, #0a76c6, #38b6f0);
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &__text {
    font-family: var(--font-data);
    font-size: 11px;
    color: #8fa5b8;
  }
}

/* ── 实时检测流 ── */
.db-stream {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -6px;
  padding: 0 6px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.14);
    border-radius: 2px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);

    &:last-child {
      border-bottom: none;
    }
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex: none;
    background: #4eae7f;
    box-shadow: 0 0 8px currentColor;
    color: #4eae7f;
  }

  &__item--warn {
    .db-stream__dot {
      background: #d9a052;
      color: #d9a052;
    }
  }

  &__item--fail {
    .db-stream__dot {
      background: #e05c52;
      color: #e05c52;
    }
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    color: #e6eef5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    font-family: var(--font-data);
    font-size: 11px;
    color: #6e8296;
  }

  &__value {
    font-family: var(--font-data);
    font-size: 16px;
    font-weight: 600;
    color: #cfe4f4;
  }

  /* 单位跟着读数走：这一列和检测记录页的“检测值(mg/L)”是同一口径，
     光看个 0.15 分不清是 mg/L 还是 %BAC，超标与否的判读会错 */
  &__unit {
    margin-left: 3px;
    font-size: 11px;
    font-weight: 400;
    color: #6e8296;
  }
}

// 新记录从上往下挤进来，配合 < 0.3s 的位移，读起来像"流"而不是"刷"
.db-stream-enter-active,
.db-stream-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.db-stream-enter {
  opacity: 0;
  transform: translateY(-10px);
}

.db-stream-leave-to {
  opacity: 0;
  transform: translateX(14px);
}

.db-empty {
  padding: 24px 0;
  text-align: center;
  font-size: 12px;
  color: #6e8296;
}

/* ── 底栏 ── */
.db3d__bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  // 图例和区间提示就一行，64 是白给的占位；收下来让面板多用
  min-height: 56px;
  padding: 0 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  pointer-events: none;
}

.db-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: #8fa5b8;

  &__title {
    color: #a9b8c6;
  }

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 1px;

    &--normal { background: #0a76c6; }
    &--warn { background: #d9a052; }
    &--fail { background: #e05c52; }
  }
}

.db3d__hint {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #6e8296;

  &-sep {
    width: 1px;
    height: 11px;
    background: rgba(255, 255, 255, 0.1);
  }
}

/* ── 加载 / 兜底 ── */
.db3d__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: rgba(10, 19, 28, 0.72);
  backdrop-filter: blur(3px);

  &-text {
    font-size: 12px;
    letter-spacing: 0.08em;
    color: #8fa5b8;
  }

  &-fail {
    max-width: 420px;
    padding: 0 20px;
    text-align: center;
    font-size: 13px;
    line-height: 1.7;
    color: #d9a052;
  }
}

.db-fade-enter-active,
.db-fade-leave-active {
  transition: opacity 0.4s ease;
}

.db-fade-enter,
.db-fade-leave-to {
  opacity: 0;
}

// 布局里读数区比整屏窄一个侧栏宽（256 + 边距），所以断点比原来整体上调 ~290
// 窄读数区先收顶栏的次要信息，保 KPI 读数不折行
@media (max-width: 1650px) {
  .db3d__subtitle {
    display: none;
  }

  .db-kpi {
    min-width: 126px;
    padding: 8px 12px 10px;
  }

  .db-kpi__value {
    font-size: 23px;
  }

  // 只留图标，窄屏下四个卡片和刷新按钮抢不了这一行宽
  .db3d__refresh span {
    display: none;
  }
}

@media (max-width: 1570px) {
  // 两侧面板收小透视角，留出中央芯片的画幅
  .db3d__panel--left,
  .db3d__panel--right {
    width: 248px;
  }

  .db3d__panel--left {
    transform: perspective(1400px) rotateY(4deg);
  }

  .db3d__panel--right {
    transform: perspective(1400px) rotateY(-4deg);
  }
}

@media (max-width: 1370px) {
  // 再窄就不摆透视面板了：两侧一挤会把中央芯片完全挡住（1370 视口 - 256 侧栏 - 32 边距 ≈ 读数区可用宽 1082）
  .db3d__panel {
    display: none;
  }
}

// 面板高度受限于读数区，而读数区又被顶栏和页脚压着，所以矮视口（笔记本、浏览器带书签栏 + 控制台）
// 要按高度再收一档行距：三张卡整列装得下，不要出现滚动条
@media (max-height: 880px) {
  .db3d__panel {
    top: 92px;
    bottom: 52px;
    gap: 8px;
  }

  .db-card {
    padding: 10px 14px 11px;
  }

  .db-card__head {
    padding-bottom: 6px;
    margin-bottom: 5px;
  }

  .db-row {
    padding: 2px 0;
  }

  .db-meter {
    margin-top: 6px;
  }
}
</style>
